/**
 * Frontend API client — the only way the UI talks to the backend (P1 wire-up).
 * In dev, REACT_APP_API_BASE can point at the standalone backend (CORS-enabled)
 * so we don't depend on the dev-server proxy for POST bodies. In prod (same origin),
 * it falls back to the relative /api path.
 */

const API_BASE = process.env.REACT_APP_API_BASE || "";

// In development the UI runs on the CRA dev server (:3000) but the API lives on the
// standalone backend (:3001, CORS-enabled). Default to it so the flow works without
// an env var. In production (same origin) API_BASE stays "" and we hit relative /api.
const isDev = typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production";
const RESOLVED_BASE = API_BASE || (isDev ? "http://localhost:3001" : "");

// NOTE: no `typeof process !== "undefined"` guard here on purpose. CRA/webpack
// DefinePlugin statically replaces `process.env.NODE_ENV` with the string
// literal "production" in prod builds, but it does NOT polyfill `process`
// itself — so any runtime `typeof process` check is false in the browser and
// the guarded version always returned false (SIM BID + fake alerts leaked to
// prod). The bare expression is safe: it is replaced at build time in the
// bundle, and `process` really exists under Jest.
export const isProduction = () => process.env.NODE_ENV === "production";

/** GET /api/board — paid listings only, sorted DESC. [] when empty (valid). */
export async function getBoard() {
  const res = await fetch(`${RESOLVED_BASE}/api/board`);
  if (!res.ok) throw new Error(`board failed: ${res.status}`);
  const data = await res.json();
  return {
    listings: Array.isArray(data.listings) ? data.listings : [],
    total_cents: data.total_cents || 0,
    progress: typeof data.progress === "number" ? data.progress : 0,
    disclaimer: data.disclaimer || "",
  };
}

/**
 * GET /api/preview?url= — server-side OG/meta auto-pull (URL auto-pull feature).
 * Returns { ok, title, description, image }. On malformed URL the backend 400s;
 * network failures / non-200 fall back to hostname. We surface the parsed object
 * and always include `title` (even if it is just the hostname).
 */
export async function fetchPreview(rawUrl) {
  try {
    const res = await fetch(`${RESOLVED_BASE}/api/preview?url=${encodeURIComponent(rawUrl)}`);
    if (!res.ok) {
      // 400 = malformed; treat as a parse failure, caller keeps the raw URL.
      return { ok: false, title: "", description: "", image: "" };
    }
    const data = await res.json().catch(() => ({}));
    return {
      ok: !!data.ok,
      title: data.title || "",
      description: data.description || "",
      image: data.image || "",
    };
  } catch {
    // Network failure / timeout — fail soft; the form keeps the raw URL.
    return { ok: false, title: "", description: "", image: "" };
  }
}

/**
 * /go/:id click-through. In dev the backend (and therefore /go) lives on :3001,
 * not the CRA :3000 origin. Resolve against the API base so the browser navigates
 * to the backend, which 302s to the real listing url.
 */
export function goUrl(id) {
  const base = RESOLVED_BASE || "";
  return base ? `${base}/go/${id}` : `/go/${id}`;
}

/**
 * POST /api/checkout — server quotes, creates a pending payment, returns a hosted
 * checkout URL. The client sends the MINIMAL body { url, amount, message?, isYou };
 * the backend auto-pulls title/description/image server-side. The UI redirects there;
 * the listing is promoted ONLY by the webhook.
 * Returns { ok, url, charge, kind, sessionId } on success, or throws on 400.
 */
export async function checkout({ url, amount, message, isYou = true }) {
  const res = await fetch(`${RESOLVED_BASE}/api/checkout`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ url, amount, message, isYou }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) {
    throw new Error(data.reason || `checkout failed: ${res.status}`);
  }
  // The backend returns a relative /pay/sandbox/... (or a real provider URL in prod).
  // Resolve it against the API base so the browser navigates to the right origin
  // (the backend serves the sandbox page; the CRA dev server would 404/SPA-fallback).
  const redirectUrl = data.url.startsWith("http")
    ? data.url
    : `${RESOLVED_BASE}${data.url}`;
  return { ...data, url: redirectUrl };
}

/** Optimistically record a click on a listing (server increments too via /go). */
export async function recordClick(id) {
  try {
    await fetch(`${RESOLVED_BASE}/api/clicks/${id}`, { method: "POST" });
  } catch {
    // best-effort; /go already increments server-side
  }
}

/** GET /api/stats — first-party visitor total + honest online count. Throws if unreachable. */
export async function getStats() {
  const res = await fetch(`${RESOLVED_BASE}/api/stats`);
  if (!res.ok) throw new Error(`stats failed: ${res.status}`);
  const data = await res.json().catch(() => ({}));
  return { visitors: Number(data.visitors) || 0, online: Number(data.online) || 0 };
}

/** POST /api/visit — increment the persisted total. Client gates once-per-day. */
export async function recordVisit() {
  const res = await fetch(`${RESOLVED_BASE}/api/visit`, { method: "POST" });
  if (!res.ok) throw new Error(`visit failed: ${res.status}`);
  const data = await res.json().catch(() => ({}));
  return { visitors: Number(data.visitors) || 0 };
}

/** POST /api/presence — heartbeat with a stable anonymous id. Returns { online }. */
export async function heartbeat(id) {
  const res = await fetch(`${RESOLVED_BASE}/api/presence`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
  if (!res.ok) throw new Error(`presence failed: ${res.status}`);
  const data = await res.json().catch(() => ({}));
  return { online: Number(data.online) || 0 };
}

/** GET /api/feed — persisted fuel + traffic events for the 3-zone live feed. */
export async function getFeed() {
  const res = await fetch(`${RESOLVED_BASE}/api/feed`);
  if (!res.ok) throw new Error(`feed failed: ${res.status}`);
  const data = await res.json().catch(() => ({}));
  return {
    fuel: Array.isArray(data.fuel) ? data.fuel : [],
    traffic: Array.isArray(data.traffic) ? data.traffic : [],
  };
}

export const api = { getBoard, fetchPreview, goUrl, checkout, recordClick, getStats, recordVisit, heartbeat, getFeed, isProduction };
export default api;
